import {
  DataGrid,
  GridRenderCellParams,
  GridPaginationModel,
} from "@mui/x-data-grid";
import WalletIcon from "assets/svg/WalletIcon";
import SearchIcon from "assets/svg/SearchIcon";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import AdminPageCardTitle from "components/AdminPageCardTitle/AdminPageCardTitle";
import { walletColumn as baseWalletColumn } from "./dataGridDef";
import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "reduxStore/store";
import { FETCH_ADMIN_WALLET } from "reduxStore/actions/authActionType";
import { useNavigate } from "react-router-dom";
import NoRowsTableDataOverlay from "components/NoRowsTableDataOverlay/NoRowsTableDataOverlay";

const WalletPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { adminWallet, loading } = useSelector(
    (state: RootState) => state.adminWallet
  );
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  // Debounce logic
  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  // Fetch wallets with search
  const fetchWallets = useCallback(
    (text: string) => {
      dispatch({
        type: FETCH_ADMIN_WALLET,
        payload: text ? { search_text: text } : {},
      });
    },
    [dispatch]
  );

  // Debounced fetch
  const debouncedFetch = useCallback(debounce(fetchWallets, 400), [
    fetchWallets,
  ]);

  useEffect(() => {
    debouncedFetch(searchText);
  }, [searchText, debouncedFetch]);

  useEffect(() => {
    // Initial fetch
    dispatch({ type: FETCH_ADMIN_WALLET, payload: {} });
  }, [dispatch]);

  const handleRowClick = (params: any) => {
    navigate(`/admin/wallet/${params.row.userId}`);
  };

  // Create walletColumn with serial number calculation
  const walletColumn = [
    {
      field: "serial",
      headerName: "No.",
      width: 70,
      renderCell: (params: GridRenderCellParams) =>
        paginationModel.page * paginationModel.pageSize +
        params.api.getRowIndexRelativeToVisibleRows(params.id) +
        1,
    },
    ...baseWalletColumn.filter((col) => col.field !== "id"), // Remove old # column
  ];

  return (
    <Box sx={styles.container}>
      <AdminPageCardTitle
        title={"User Wallets"}
        icon={<WalletIcon width={28} height={28} />}
      />
      <Box sx={{ mb: 2, mt: 1, display: "flex", justifyContent: "flex-end" }}>
        <TextField
          placeholder="Search by name, email or wallet address"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          sx={{
            maxWidth: 280,
            "& .MuiInputBase-input::placeholder": { fontSize: 13 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon width={20} height={20} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <DataGrid
        rows={adminWallet?.data || []}
        columns={walletColumn}
        getRowId={(row) => row.walletId}
        onRowClick={handleRowClick}
        loading={loading}
        slots={{
          noRowsOverlay: () => (
            <NoRowsTableDataOverlay title={"No wallet data found"} />
          ),
        }}
        sx={{
          border: 0,
          "& .MuiDataGrid-row:hover": {
            cursor: "pointer",
          },
        }}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: paginationModel.pageSize,
            },
          },
        }}
        pageSizeOptions={[10]}
        disableRowSelectionOnClick
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
      />
    </Box>
  );
};
export default WalletPage;
const styles = {
  container: {
    flexGrow: 1,
    borderRadius: 3,
    boxShadow: `rgba(8, 60, 61, 0.27) 0px 1px 5px 0px, rgba(8, 60, 61, 0.06) 0px 1px 5px 0px`,
    backgroundColor: "rgb(255, 255, 255)",
    p: 3,
    ml: { xs: 0, md: -5 },
  },
};
